import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, RefreshCw, ShieldAlert, ArrowDownLeft, ArrowUpRight } from "lucide-react";

const TONE = {
  PASS: "bg-emerald-100 text-emerald-800 border-emerald-300",
  READY: "bg-emerald-100 text-emerald-800 border-emerald-300",
  FAIL: "bg-red-100 text-red-800 border-red-300",
  BLOCKED: "bg-red-100 text-red-800 border-red-300",
  ERROR: "bg-orange-100 text-orange-900 border-orange-300",
  UNKNOWN: "bg-slate-100 text-slate-700 border-slate-300",
  NOT_CHECKED: "bg-slate-50 text-slate-500 border-slate-200",
  INFO: "bg-sky-50 text-sky-800 border-sky-200",
};
const tone = (s) => TONE[String(s || "").toUpperCase()] || TONE.INFO;
const tid = (s) => String(s).replace(/[^a-z0-9]+/gi, "-").toLowerCase();

const SCENARIOS = ["healthy", "token_expired", "wrong_app", "waba_no_permission",
  "not_subscribed", "messages_field_missing", "subscription_info_missing",
  "rate_limited", "network_error"];

function MetaError({ e }) {
  if (!e) return null;
  return (
    <div className="mt-1 rounded bg-orange-50 border border-orange-200 px-1.5 py-1 text-[11px] text-orange-900"
      data-testid="wi-diag-meta-error">
      <div className="font-mono break-all">
        HTTP {e.http_status ?? "—"} · code={e.code ?? "-"} · subcode={e.subcode ?? "-"}
        {" "}· type={e.type ?? "-"} · trace={e.fbtrace_id ?? "-"}
      </div>
      <div className="break-all">{e.message}</div>
      {e.hint && <div className="italic opacity-80">{e.hint}</div>}
    </div>
  );
}

function CheckRow({ c }) {
  return (
    <div className="flex items-start justify-between gap-2 border rounded px-2 py-1.5 text-xs"
      data-testid={`wi-monitor-check-${tid(c.name)}`}>
      <div className="flex-1 min-w-0">
        <div className="font-medium">{c.name}</div>
        {c.detail && <div className="text-slate-500 text-[11px] break-all">{c.detail}</div>}
        <MetaError e={c.meta_error} />
      </div>
      <Badge className={tone(c.status)} data-testid={`wi-monitor-status-${tid(c.name)}`}>
        {c.status === "INFO" ? (c.value || "—") : c.status}
      </Badge>
    </div>
  );
}

function ReadinessCard({ title, icon: Icon, r, testid }) {
  if (!r) return null;
  return (
    <div className="border rounded p-2 space-y-1" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold flex items-center gap-1">
          <Icon className="w-3.5 h-3.5" /> {title}
        </div>
        <Badge className={tone(r.status)} data-testid={`${testid}-status`}>{r.status}</Badge>
      </div>
      <ul className="text-[11px] space-y-0.5">
        {r.requirements.map((q) => (
          <li key={q.key} className="flex justify-between gap-2">
            <span>{q.name}</span>
            <span className={`px-1 rounded border ${tone(q.status)}`}>{q.status}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SubscribeDialog({ open, onClose, session, centre, scenario, onDone }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const phrase = `SUBSCRIBE ${centre}`;
  const go = async () => {
    setBusy(true); setRes(null);
    try {
      const r = await api.post("/whatsapp-intel/pilot/subscribe-waba", {
        token: session.token, center: centre, confirm: typed,
        mock_scenario: scenario || null });
      setRes(r.data);
      if (r.data.ok) { toast.success("Meta accepted the subscription"); onDone(); }
    } catch (e) {
      setRes({ ok: false, error: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setTyped(""); setRes(null); onClose(); } }}>
      <DialogContent data-testid="wi-subscribe-dialog">
        <DialogHeader>
          <DialogTitle>Change Meta subscription?</DialogTitle>
          <DialogDescription>Write action on Meta — requires typed confirmation.</DialogDescription>
        </DialogHeader>
        <div className="text-sm space-y-2">
          <p>This calls <code>POST /{"{waba_id}"}/subscribed_apps</code> on Meta and
            subscribes the app that owns the stored Access Token to the {centre} WABA.
            It is a <strong>write action on Meta</strong>.</p>
          {scenario && <Badge className="bg-violet-100 text-violet-800">MOCK: {scenario}</Badge>}
          <p>Type <code className="font-semibold">{phrase}</code> to confirm.</p>
          <Input value={typed} onChange={(e) => setTyped(e.target.value)}
            data-testid="wi-subscribe-confirm-input" />
          {res && (
            <div className={`text-xs rounded border px-2 py-1 ${res.ok
              ? "bg-emerald-50 border-emerald-300" : "bg-red-50 border-red-300"}`}
              data-testid="wi-monitor-subscribe-result">
              <div><strong>HTTP {res.http_status ?? "—"}</strong> {res.ok ? "SUCCESS" : "FAILED"}</div>
              <div className="break-all">{res.message || res.error}</div>
              {res.error_code != null && (
                <div className="font-mono text-[11px]">code={res.error_code} · subcode=
                  {res.error_subcode ?? "-"} · trace={res.fbtrace_id ?? "-"}</div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="wi-subscribe-cancel">Cancel</Button>
          <Button onClick={go} disabled={busy || typed.trim().toUpperCase() !== phrase}
            data-testid="wi-subscribe-confirm-btn">
            {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}Subscribe
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MetaDiagnosticsPanel({ session, diag, loading, centreOptions, activeCentre,
  onPick, onRefresh, mockEnabled, scenario, setScenario }) {
  const [showSub, setShowSub] = useState(false);
  const groups = { meta: [], local: [], activity: [] };
  (diag?.checks || []).forEach((c) => (groups[c.group] || groups.activity).push(c));
  const subCheck = (diag?.checks || []).find((c) => c.key === "waba_app_subscription");
  return (
    <Card className="border-emerald-300" data-testid="wi-webhook-monitor">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-emerald-700" /> META CONNECTION DIAGNOSTICS
          <Badge variant="outline" data-testid="wi-diag-readonly-badge">READ-ONLY</Badge>
          {activeCentre && <Badge variant="outline">{activeCentre}</Badge>}
        </CardTitle>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={activeCentre || ""} onValueChange={onPick}>
            <SelectTrigger className="w-44" data-testid="wi-monitor-centre">
              <SelectValue placeholder="Select centre" />
            </SelectTrigger>
            <SelectContent>
              {centreOptions.map((c) => (
                <SelectItem key={c.center} value={c.center}>{c.center}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {mockEnabled && (
            <Select value={scenario || "LIVE"} onValueChange={(v) => setScenario(v === "LIVE" ? "" : v)}>
              <SelectTrigger className="w-52" data-testid="wi-monitor-mock-scenario">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="LIVE">Live Meta (no mock)</SelectItem>
                {SCENARIOS.map((s) => <SelectItem key={s} value={s}>MOCK · {s}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button size="sm" variant="outline" onClick={onRefresh}
            disabled={loading || !activeCentre} data-testid="wi-monitor-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh Diagnostics
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!diag && !loading && (
          <div className="text-xs text-slate-500 italic">
            Pick a centre and click <em>Refresh Diagnostics</em>. Refresh only reads
            from Meta — it never changes settings or subscriptions.
          </div>
        )}
        {loading && !diag && (
          <div className="text-xs text-slate-500 flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Probing Meta Graph API…
          </div>
        )}
        {diag && (
          <div className="space-y-3">
            <div className="text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
              <span>Callback URL: <code className="bg-slate-100 px-1 rounded"
                data-testid="wi-monitor-callback">{diag.callback_url || "—"}</code></span>
              <span>Meta App ID: <code data-testid="wi-diag-app-id">{diag.meta_app_id || "not set"}</code></span>
              <span>Graph {diag.graph_version}</span>
              <span>Checked {(diag.checked_at || "").slice(0, 19).replace("T", " ")} UTC</span>
              {diag.mock_scenario && (
                <Badge className="bg-violet-100 text-violet-800" data-testid="wi-diag-mock-badge">
                  MOCKED META RESPONSE · {diag.mock_scenario}
                </Badge>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <ReadinessCard title="Inbound readiness (receive messages)" icon={ArrowDownLeft}
                r={diag.inbound_readiness} testid="wi-diag-inbound" />
              <ReadinessCard title="Outbound readiness (send messages)" icon={ArrowUpRight}
                r={diag.outbound_readiness} testid="wi-diag-outbound" />
            </div>
            {[["meta", "Meta Graph API checks"], ["local", "IntraPB configuration"],
              ["activity", "Webhook activity"]].map(([g, label]) => (
              <div key={g} data-testid={`wi-diag-group-${g}`}>
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{label}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {groups[g].map((c) => <CheckRow key={c.key || c.name} c={c} />)}
                </div>
              </div>
            ))}
            {subCheck && subCheck.status !== "PASS" && (
              <div className="border-t pt-2 flex items-center justify-between gap-2 flex-wrap">
                <div className="text-[11px] text-slate-600">
                  The intended app is not confirmed as subscribed to this WABA. Subscribing
                  changes Meta configuration and needs explicit confirmation.
                </div>
                <Button size="sm" variant="outline" onClick={() => setShowSub(true)}
                  data-testid="wi-monitor-subscribe">Subscribe app to {activeCentre} WABA…</Button>
              </div>
            )}
          </div>
        )}
        <SubscribeDialog open={showSub} onClose={() => setShowSub(false)} session={session}
          centre={activeCentre} scenario={scenario} onDone={onRefresh} />
      </CardContent>
    </Card>
  );
}
